import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { LinearInterpSingleVal } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import { PressurePoint } from '../../models/internal-pressure-profile.model';

export function createCustomIppColumns(): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'measuredDepth',
      header: 'MD',
      unitKey: 'longLengths',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    },
    {
      kind: 'number',
      field: 'pressure',
      header: 'Pressure',
      unitKey: 'pressure',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    }
  ];
}

export function createCustomIppRow(data?: Partial<PressurePoint>): FormGroup {
  return new FormGroup({
    measuredDepth: new FormControl<number | null>(data?.measuredDepth ?? null, [Validators.required]),
    pressure: new FormControl<number | null>(data?.pressure ?? null, [Validators.required])
  });
}

export function isCustomIppRowEmpty(row: FormGroup): boolean {
  const md = row.get('measuredDepth')?.value;
  const pressure = row.get('pressure')?.value;
  const isMdEmpty = md === null || md === undefined || md === '' || Number.isNaN(Number(md));
  const isPressureEmpty = pressure === null || pressure === undefined || pressure === '' || Number.isNaN(Number(pressure));
  return isMdEmpty && isPressureEmpty;
}

// Keeps a single trailing blank row so users can add rows by typing, without needing the context menu.
export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createCustomIppRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isCustomIppRowEmpty(formArray.at(formArray.length - 1)) &&
    isCustomIppRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  if (!isCustomIppRowEmpty(formArray.at(formArray.length - 1))) {
    formArray.push(createCustomIppRow());
  }
}

export function populateCustomIppFormArray(
  formArray: FormArray<FormGroup>,
  points: readonly PressurePoint[]
): void {
  formArray.clear();
  points.forEach((point) => formArray.push(createCustomIppRow(point)));
  ensureTrailingInputRow(formArray);
}

export function extractCustomIppRows(formArray: FormArray<FormGroup>): PressurePoint[] {
  return formArray.controls
    .filter((control) => !isCustomIppRowEmpty(control as FormGroup))
    .map((control) => ({
      measuredDepth: Number(control.get('measuredDepth')?.value ?? 0),
      pressure: Number(control.get('pressure')?.value ?? 0)
    }));
}

export function findRowIndexNearDepth(
  formArray: FormArray<FormGroup>,
  depth: number,
  tolerance = 0.0001
): number {
  return formArray.controls.findIndex((control) => {
    const value = control.get('measuredDepth')?.value;
    return value !== null && value !== undefined && value !== '' && Math.abs(Number(value) - depth) < tolerance;
  });
}

export function isCustomIppPlugDepthRow(
  row: FormGroup,
  plugDepth: number | null | undefined,
  tolerance = 0.0001
): boolean {
  if (plugDepth === null || plugDepth === undefined) {
    return false;
  }

  const measuredDepth = row.get('measuredDepth')?.value;
  return measuredDepth !== null
    && measuredDepth !== undefined
    && measuredDepth !== ''
    && Math.abs(Number(measuredDepth) - plugDepth) < tolerance;
}

interface CustomIppPlugRowContext {
  readonly relation: 'plug' | 'above';
}

function getCustomIppPlugRowContext(
  rows: readonly FormGroup[],
  rowIndex: number,
  hasPlug: boolean,
  plugDepth: number | null | undefined,
  tolerance: number
): CustomIppPlugRowContext | null {
  if (!hasPlug || plugDepth === null || plugDepth === undefined) {
    return null;
  }

  const row = rows[rowIndex];
  if (row && isCustomIppPlugDepthRow(row, plugDepth, tolerance)) {
    return { relation: 'plug' };
  }

  const nextRow = rows[rowIndex + 1];
  if (nextRow && isCustomIppPlugDepthRow(nextRow, plugDepth, tolerance)) {
    return { relation: 'above' };
  }

  return null;
}

export function isCustomIppProtectedRow(
  rows: readonly FormGroup[],
  rowIndex: number,
  hasPlug: boolean,
  plugDepth: number | null | undefined,
  tolerance = 0.0001
): boolean {
  return getCustomIppPlugRowContext(rows, rowIndex, hasPlug, plugDepth, tolerance) !== null;
}

// Labels the plug row itself; the companion row above it falls through to the disabled delete icon.
export function getCustomIppRowActionLabel(
  rows: readonly FormGroup[],
  rowIndex: number,
  hasPlug: boolean,
  plugDepth: number | null | undefined,
  tolerance = 0.0001
): string | null {
  const context = getCustomIppPlugRowContext(rows, rowIndex, hasPlug, plugDepth, tolerance);
  if (!context) {
    return null;
  }

  return context.relation === 'plug' ? 'Plug' : null;
}

export function getCustomIppRowActionIconSrc(
  rows: readonly FormGroup[],
  rowIndex: number,
  hasPlug: boolean,
  plugDepth: number | null | undefined,
  tolerance = 0.0001
): string | null {
  return getCustomIppPlugRowContext(rows, rowIndex, hasPlug, plugDepth, tolerance)?.relation === 'plug'
    ? '../../assets/icons/plug-24x24.svg'
    : null;
}

// Matches ng-table-grid's per-cell pTooltip wording ("Depth/Pressure Below/Above Plug").
export function getCustomIppCellTooltip(
  rows: readonly FormGroup[],
  rowIndex: number,
  columnField: string,
  hasPlug: boolean,
  plugDepth: number | null | undefined,
  tolerance = 0.0001
): string | null {
  const context = getCustomIppPlugRowContext(rows, rowIndex, hasPlug, plugDepth, tolerance);
  if (!context) {
    return null;
  }

  const valueLabel = columnField === 'measuredDepth' ? 'Depth' : 'Pressure';
  const relationLabel = context.relation === 'plug' ? 'Below' : 'Above';
  return `${valueLabel} ${relationLabel} Plug`;
}

export interface CustomIppPlugAnchorResult {
  readonly points: PressurePoint[];
  readonly syntheticDepths: number[];
}

// Ensures a point exists at the plug depth and at the interpolated depth just above it,
// dropping any anchor points that were inserted for a previous plug position first.
// Both checks are evaluated against the pre-synthesis points (matching the packer logic in
// custom-epp.component.ts) so inserting the plug-depth point doesn't mask the need for the
// point above it.
export function ensureCustomIppPlugAnchors(
  points: readonly PressurePoint[],
  plugDepth: number,
  depthAbovePlug: number,
  previousSyntheticDepths: readonly number[] = [],
  aboveTolerance = 0.01
): CustomIppPlugAnchorResult {
  const basePoints = previousSyntheticDepths.length > 0
    ? points.filter(p => !previousSyntheticDepths.some(d => Math.abs(d - p.measuredDepth) < 0.01))
    : [...points];

  const syntheticDepths: number[] = [];
  const additions: PressurePoint[] = [];

  const hasPointAtPlug = basePoints.some(p => Math.abs(p.measuredDepth - plugDepth) < 0.00001);
  if (!hasPointAtPlug) {
    const pressureAtPlug = +LinearInterpSingleVal(
      plugDepth,
      basePoints.map(p => p.measuredDepth),
      basePoints.map(p => p.pressure)
    );
    additions.push({ measuredDepth: plugDepth, pressure: +pressureAtPlug.toFixed(3) });
    syntheticDepths.push(plugDepth);
  }

  const depthAbove = plugDepth - depthAbovePlug;
  const hasPointAbove = basePoints.some(p => Math.abs(p.measuredDepth - depthAbove) <= aboveTolerance);
  if (!hasPointAbove) {
    const pressureAbovePlug = +LinearInterpSingleVal(
      depthAbove,
      basePoints.map(p => p.measuredDepth),
      basePoints.map(p => p.pressure)
    );
    additions.push({ measuredDepth: depthAbove, pressure: +pressureAbovePlug.toFixed(3) });
    syntheticDepths.push(depthAbove);
  }

  const workingPoints = [...basePoints, ...additions].sort((a, b) => a.measuredDepth - b.measuredDepth);

  return { points: workingPoints, syntheticDepths };
}


